import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { utils } from '@atlaskit/util-service-support';
import { generateHash } from '../../../utils/caching/common/key-generator';
import { promiseCache } from '../../../utils/caching/persist-refresh';
import { DegradablePromise } from '../../utils/degradable-promise';
var RELATIVE_URL = '/gateway/api/xpsearch-aggregator/configuration/v1';
var RELATIVE_URL_V2 = '/gateway/api/xpsearch-aggregator/api/v2/configuration';
var RELATIVE_3P_URL = '/gateway/api/third-party-configuration/connected-data-sources';
var RELATIVE_LOOM_AUTH_URL = '/gateway/api/loom-auth/api/internal/identity/user-status';
export var SEARCH_CONFIG = 'search-config';
export var SEARCH_3P_CONFIG = 'search-3p-config';
export var LOOM_USER_AUTH = 'loom-user-auth';
var _promiseCache = promiseCache({
    name: SEARCH_CONFIG
  }),
  refreshOnAccessDecoratorSearch = _promiseCache.refreshOnAccessDecorator;
var _promiseCache2 = promiseCache({
    name: SEARCH_3P_CONFIG,
    expiration: 1000 * 60 * 5
  }),
  refreshOnAccessDecorator3pSearch = _promiseCache2.refreshOnAccessDecorator;
var _promiseCache3 = promiseCache({
    name: LOOM_USER_AUTH
  }),
  refreshOnAccessDecoratorUserLoomAuth = _promiseCache3.refreshOnAccessDecorator;
var SITELESS_PRODUCTS = ['trello', 'bitbucket'];

/**
 * The tier of certain products
 * @key Product Key
 * @value Product Tier
 * @example `{ 'confluence': "PREMIUM" }`
 */

/**
 * Defines where a product's data is sourced from
 * @key Product Key
 * @value List of data regions for the product
 * @example `{ 'confluence': ["us-west-2"], 'compass': ["us-east-1"] }`
 */

/**
 * Defines rovo specific features
 * @example `{ 'scas': true }`
 */

// https://developer.atlassian.com/platform/cross-product-search/rest/api-group-other-operations/#api-configuration-v1-get
export var getSearchConfig = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
    var _ref$absoluteUrl, absoluteUrl, hostName, cloudId, userId, onlyRefreshIfExpired, expiration, url, options, key, supplier;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          _ref$absoluteUrl = _ref.absoluteUrl, absoluteUrl = _ref$absoluteUrl === void 0 ? '' : _ref$absoluteUrl, hostName = _ref.hostName, cloudId = _ref.cloudId, userId = _ref.userId, onlyRefreshIfExpired = _ref.onlyRefreshIfExpired, expiration = _ref.expiration;
          url = absoluteUrl || "".concat(hostName || '').concat(RELATIVE_URL);
          options = {
            requestInit: {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              }
            }
          };
          key = generateHash("1p|".concat(userId, "|").concat(cloudId));
          supplier = function supplier() {
            return utils.requestService({
              url: url
            }, options).then(function (data) {
              var resources = data.resources.filter(function (resource) {
                return cloudId ? resource.id.includes(cloudId) || SITELESS_PRODUCTS.find(function (product) {
                  return resource.id.includes(product);
                }) // Allow access to products that are not associated with a site
                : true;
              });
              return {
                resources: resources,
                intercomHmac: data.intercomHmac || '',
                siteMetadata: data.siteMetadata || []
              };
            });
          };
          return _context.abrupt("return", refreshOnAccessDecoratorSearch({
            key: key.toString(),
            supplier: supplier,
            onlyRefreshIfExpired: onlyRefreshIfExpired,
            expiration: expiration
          }));
        case 6:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function getSearchConfig(_x) {
    return _ref2.apply(this, arguments);
  };
}();

// https://developer.atlassian.com/platform/cross-product-search/rest/api-group-other-operations/#api-api-v2-configuration-cloudid-get
export var getSearchConfigV2 = /*#__PURE__*/function () {
  var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(_ref3) {
    var _ref3$absoluteUrl, absoluteUrl, hostName, cloudId, userId, onlyRefreshIfExpired, expiration, _ref3$degradationCall, degradationCallback, url, options, key, supplier;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          _ref3$absoluteUrl = _ref3.absoluteUrl, absoluteUrl = _ref3$absoluteUrl === void 0 ? '' : _ref3$absoluteUrl, hostName = _ref3.hostName, cloudId = _ref3.cloudId, userId = _ref3.userId, onlyRefreshIfExpired = _ref3.onlyRefreshIfExpired, expiration = _ref3.expiration, _ref3$degradationCall = _ref3.degradationCallback, degradationCallback = _ref3$degradationCall === void 0 ? function () {} : _ref3$degradationCall;
          url = absoluteUrl || "".concat(hostName || '').concat(RELATIVE_URL_V2, "/").concat(cloudId);
          options = {
            requestInit: {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              }
            }
          };
          key = generateHash("1p|".concat(userId, "|").concat(cloudId, "|v2"));
          supplier = function supplier() {
            var searchConfigPromise = utils.requestService({
              url: url
            }, options).then(function (data) {
              return {
                resources: data.resources,
                intercomHmac: data.intercomHmac || '',
                siteMetadata: data.siteMetadata || []
              };
            });

            // Two cases here, due to the promiseCache from refreshOnAccessDecoratorSearch below
            // 1. Cache-miss: we of course want to handle degradation!
            // 2. Cache-hit: we actually still want to handle degradation, because if we can't refresh the cache due to a slow dependency, that is a signal we need in healthchecks
            // The alternate approach here would be to wrap the promiseCache in the DegradablePromise, but then we'd only get degradation pings on cache-miss.
            var degrader = new DegradablePromise(searchConfigPromise, degradationCallback);
            return degrader.promise();
          };
          return _context2.abrupt("return", refreshOnAccessDecoratorSearch({
            key: key.toString(),
            supplier: supplier,
            onlyRefreshIfExpired: onlyRefreshIfExpired,
            expiration: expiration
          }));
        case 6:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  }));
  return function getSearchConfigV2(_x2) {
    return _ref4.apply(this, arguments);
  };
}();

/**
 * Sample response:
 * [
      {
        "type": "ai-3p-connector:google-workspace",
        "location": "ari:third-party:google::site/DUMMY-0c418054-69d8-4a6c-bec6-8ef053070a36"
      }
    ]
 */

export var get3pSearchConfig = /*#__PURE__*/function () {
  var _ref6 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(_ref5) {
    var _ref5$absoluteUrl, absoluteUrl, cloudId, userId, onlyRefreshIfExpired, expiration, _ref5$degradationCall, degradationCallback, baseUrl, url, options, key, supplier;
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) switch (_context3.prev = _context3.next) {
        case 0:
          _ref5$absoluteUrl = _ref5.absoluteUrl, absoluteUrl = _ref5$absoluteUrl === void 0 ? '' : _ref5$absoluteUrl, cloudId = _ref5.cloudId, userId = _ref5.userId, onlyRefreshIfExpired = _ref5.onlyRefreshIfExpired, expiration = _ref5.expiration, _ref5$degradationCall = _ref5.degradationCallback, degradationCallback = _ref5$degradationCall === void 0 ? function () {} : _ref5$degradationCall;
          baseUrl = absoluteUrl || RELATIVE_3P_URL;
          url = "".concat(baseUrl, "/").concat(cloudId);
          options = {
            requestInit: {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              }
            }
          };
          key = generate3pHash(userId || '', cloudId || '');
          supplier = function supplier() {
            var searchConfig3PPromise = utils.requestService({
              url: url
            }, options);

            // Two cases here, due to the promiseCache from refreshOnAccessDecorator3pSearch below
            // 1. Cache-miss: we of course want to handle degradation!
            // 2. Cache-hit: we actually still want to handle degradation, because if we can't refresh the cache due to a slow dependency, that is a signal we need in healthchecks
            // The alternate approach here would be to wrap the promiseCache in the DegradablePromise, but then we'd only get degradation pings on cache-miss.
            var degrader = new DegradablePromise(searchConfig3PPromise, degradationCallback);
            return degrader.promise();
          };
          return _context3.abrupt("return", refreshOnAccessDecorator3pSearch({
            key: key.toString(),
            supplier: supplier,
            returnExpired: false,
            onlyRefreshIfExpired: onlyRefreshIfExpired,
            expiration: expiration
          }));
        case 7:
        case "end":
          return _context3.stop();
      }
    }, _callee3);
  }));
  return function get3pSearchConfig(_x3) {
    return _ref6.apply(this, arguments);
  };
}();
export var generate3pHash = function generate3pHash(userId, cloudId) {
  return generateHash("3p|".concat(userId, "|").concat(cloudId));
};

/**
 * Sample response:
 *
      {
        "status": "mastered",
      }
 */

export var getUserLoomAuthStatus = /*#__PURE__*/function () {
  var _ref8 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(_ref7) {
    var cloudId, userId, url, options, key, supplier;
    return _regeneratorRuntime.wrap(function _callee4$(_context4) {
      while (1) switch (_context4.prev = _context4.next) {
        case 0:
          cloudId = _ref7.cloudId, userId = _ref7.userId;
          url = RELATIVE_LOOM_AUTH_URL;
          options = {
            requestInit: {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              }
            }
          };
          key = generateLoomAuthHash(userId || '', cloudId || '');
          supplier = function supplier() {
            return utils.requestService({
              url: url
            }, options);
          };
          return _context4.abrupt("return", refreshOnAccessDecoratorUserLoomAuth({
            key: key.toString(),
            supplier: supplier,
            returnExpired: false
          }));
        case 6:
        case "end":
          return _context4.stop();
      }
    }, _callee4);
  }));
  return function getUserLoomAuthStatus(_x4) {
    return _ref8.apply(this, arguments);
  };
}();
export var generateLoomAuthHash = function generateLoomAuthHash(userId, cloudId) {
  return generateHash("loom|".concat(userId, "|").concat(cloudId));
};